'use client';

import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { useGlobalSearchOptional } from './global-search-provider';

interface GlobalSearchTriggerProps {
  /**
   * `bar` (default) renders the full search-input affordance for desktop
   * headers; `icon` renders a compact icon button for tight mobile headers.
   */
  variant?: 'bar' | 'icon';
  className?: string;
}

/**
 * The always-present header entry point for global search. A dumb button that
 * opens the shared overlay owned by `GlobalSearchProvider` — the Cmd+K listener
 * and the dialog live there, so the trigger can be mounted on many surfaces
 * without duplicating either (R1).
 *
 * Uses the optional (non-throwing) context hook: this trigger is rendered by
 * the marketing `SiteHeader`, which the root `not-found.tsx` mounts OUTSIDE
 * the app-shell provider tree. When no provider is in scope (e.g. the bare
 * 404 boundary that a `POST` to an action-less page falls through to), the
 * trigger renders as an inert no-op instead of throwing (Sentry BALLEE-EZ).
 */
export function GlobalSearchTrigger({
  variant = 'bar',
  className,
}: GlobalSearchTriggerProps) {
  const { t } = useTranslation('search');
  const search = useGlobalSearchOptional();
  const setOpen = search?.setOpen;

  if (variant === 'icon') {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={className}
        aria-label={t('triggerLabel')}
        onClick={() => setOpen?.(true)}
      >
        <Search className="h-5 w-5" />
      </Button>
    );
  }

  // `bar` is a REAL input: typing the first character opens the shared
  // overlay pre-seeded with it (openWithQuery), so users can just click and
  // type instead of learning ⌘K. The local input never keeps text — the
  // overlay's own input takes over immediately.
  return (
    <div
      className={cn(
        'border-input bg-background text-muted-foreground focus-within:ring-ring hover:bg-accent/50 flex h-9 w-full max-w-xs items-center gap-2 rounded-md border px-3 text-sm transition-colors focus-within:ring-1',
        className,
      )}
    >
      <Search className="h-4 w-4 flex-shrink-0" />
      {/* eslint-disable-next-line kit-components/use-kit-primitives -- bare transparent input composes the custom bordered search-bar affordance; the Input chrome (border, padding, height, shadow) would double the wrapper's border and break the composed layout */}
      <input
        type="text"
        value=""
        placeholder={t('triggerPlaceholder')}
        aria-label={t('triggerLabel')}
        className="placeholder:text-muted-foreground h-full w-full min-w-0 flex-1 bg-transparent outline-none"
        onChange={(e) => {
          const typed = e.target.value;
          if (!typed) return;
          if (search?.openWithQuery) {
            search.openWithQuery(typed);
          } else {
            setOpen?.(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            setOpen?.(true);
          }
        }}
        onClick={() => setOpen?.(true)}
      />
      <kbd className="bg-muted text-muted-foreground pointer-events-none hidden h-5 items-center gap-0.5 rounded border px-1.5 font-mono text-[10px] font-medium sm:flex">
        ⌘K
      </kbd>
    </div>
  );
}
